import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['select', 'trigger', 'panel', 'input', 'list', 'empty']

  connect() {
    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundReposition = this.reposition.bind(this)
    this.highlightedRow = null
    this.rows = this.buildRows()
    this.rows.forEach((row) => this.listTarget.appendChild(row))
  }

  disconnect() {
    document.removeEventListener('mousedown', this.boundClickOutside)
    window.removeEventListener('resize', this.boundReposition)
    window.removeEventListener('scroll', this.boundReposition, true)
  }

  buildRows() {
    const seen = new Set()
    const priorityRows = []
    const restRows = []
    let afterSeparator = false

    Array.from(this.selectTarget.childNodes).forEach((node) => {
      if (node.tagName === 'HR') {
        afterSeparator = true
        return
      }

      if (node.tagName !== 'OPTION' || !node.value || seen.has(node.value)) return
      seen.add(node.value)

      const row = this.buildRow(node)
      if (afterSeparator) {
        restRows.push(row)
      } else {
        priorityRows.push(row)
      }
    })

    restRows.sort((a, b) => a.dataset.name.localeCompare(b.dataset.name))

    return [...priorityRows, ...restRows]
  }

  buildRow(option) {
    const display = option.dataset.display || ''
    const flag = display.split(' ')[0] || ''
    const dialCode = `+${option.dataset.countryCode || ''}`
    const name = option.dataset.countryName || option.textContent.trim()

    const row = document.createElement('button')
    row.type = 'button'
    row.dataset.countrySearchTarget = 'option'
    row.dataset.value = option.value
    row.dataset.name = name
    row.dataset.dialCode = option.dataset.countryCode || ''
    row.dataset.action = 'click->country-search#select'
    row.className = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text hover:bg-raised cursor-pointer'

    const label = document.createElement('span')
    label.className = 'flex-1 truncate'
    label.textContent = `${flag} ${name}`.trim()

    const code = document.createElement('span')
    code.className = 'font-mono text-text-muted'
    code.textContent = dialCode

    row.appendChild(label)
    row.appendChild(code)

    return row
  }

  toggle(event) {
    event.preventDefault()

    if (this.panelTarget.classList.contains('hidden')) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.panelTarget.classList.remove('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.reposition()
    this.inputTarget.value = ''
    this.applyFilter('')
    this.inputTarget.focus()

    document.addEventListener('mousedown', this.boundClickOutside)
    window.addEventListener('resize', this.boundReposition)
    window.addEventListener('scroll', this.boundReposition, true)
  }

  close() {
    this.panelTarget.classList.add('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'false')

    document.removeEventListener('mousedown', this.boundClickOutside)
    window.removeEventListener('resize', this.boundReposition)
    window.removeEventListener('scroll', this.boundReposition, true)
  }

  clickOutside(event) {
    if (this.panelTarget.contains(event.target) || this.triggerTarget.contains(event.target)) return

    this.close()
  }

  reposition() {
    const rect = this.triggerTarget.getBoundingClientRect()
    this.panelTarget.style.top = `${rect.bottom + 4}px`
    this.panelTarget.style.left = `${rect.left}px`
  }

  filter() {
    this.applyFilter(this.inputTarget.value)
  }

  applyFilter(rawQuery) {
    const query = this.normalize(rawQuery.trim())

    if (!query) {
      this.rows.forEach((row) => {
        row.classList.remove('hidden')
        this.listTarget.appendChild(row)
      })
      this.renderEmptyState(false)
      const selectedRow = this.rows.find((row) => row.dataset.value === this.selectTarget.value) || this.rows[0] || null
      this.highlightRow(selectedRow)
      if (selectedRow) selectedRow.scrollIntoView({ block: 'nearest' })
      return
    }

    const prefixMatches = []
    const substringMatches = []
    const dialCodeQuery = query.replace(/^\+/, '')
    const isDialCodeQuery = /^\d+$/.test(dialCodeQuery)

    this.rows.forEach((row) => {
      const name = this.normalize(row.dataset.name)

      if (isDialCodeQuery && row.dataset.dialCode === dialCodeQuery) {
        prefixMatches.push(row)
      } else if (isDialCodeQuery && row.dataset.dialCode.startsWith(dialCodeQuery)) {
        substringMatches.push(row)
      } else if (name.startsWith(query)) {
        prefixMatches.push(row)
      } else if (name.includes(query)) {
        substringMatches.push(row)
      } else {
        row.classList.add('hidden')
      }
    })

    const ordered = [...prefixMatches, ...substringMatches]
    ordered.forEach((row) => {
      row.classList.remove('hidden')
      this.listTarget.appendChild(row)
    })

    this.renderEmptyState(ordered.length === 0)
    this.highlightRow(ordered[0] || null)
  }

  normalize(value) {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  }

  renderEmptyState(show) {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.classList.toggle('hidden', !show)
  }

  visibleRows() {
    return this.rows.filter((row) => !row.classList.contains('hidden'))
  }

  highlightRow(row) {
    this.rows.forEach((r) => r.classList.remove('bg-raised'))
    if (row) row.classList.add('bg-raised')
    this.highlightedRow = row
  }

  keydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      this.close()
      this.triggerTarget.focus()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.moveHighlight(1)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.moveHighlight(-1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (this.highlightedRow) this.applySelection(this.highlightedRow)
    }
  }

  moveHighlight(step) {
    const visible = this.visibleRows()
    if (!visible.length) return

    const currentIndex = this.highlightedRow ? visible.indexOf(this.highlightedRow) : -1
    const nextIndex = (currentIndex + step + visible.length) % visible.length
    const next = visible[nextIndex]
    this.highlightRow(next)
    next.scrollIntoView({ block: 'nearest' })
  }

  select(event) {
    event.preventDefault()
    this.applySelection(event.currentTarget)
  }

  applySelection(row) {
    this.selectTarget.value = row.dataset.value
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.close()
  }
}
